import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Receipt, Calendar, Percent, User as UserIcon, FileText,
  ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Info,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatMXN, toISODate } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
import {
  useGenerarFacturaContrato,
  type GenerarFacturaContratoInput,
} from "@/features/contratos/api";
import type { ContratoDetail } from "@/features/contratos/schemas";

type ProximoCiclo = {
  hoy: string; fecha_corte: number; vencimiento: string; periodo_inicio: string; periodo_fin: string;
  dias_restantes: number; dias_anticipacion: number; ya_emitida: boolean;
  facturar_desde: string | null; saltado_por_facturar_desde: boolean; poller_lo_emitiria_hoy: boolean;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  contrato: ContratoDetail;
  /** Para navegar a la factura recién creada y poder volver al contrato. */
  clienteRazonSocial?: string;
  planNombre?: string;
  planPrecio?: string | number;
};

/**
 * Wizard de 2 pasos para emitir factura manual desde un contrato.
 *
 *   Paso 1: form con campos opcionales (periodo, vencimiento, descuento).
 *           Todos con defaults sensatos calculados desde el contrato.
 *   Paso 2: preview de confirmación con el cálculo del total.
 *           Botón "Sí, generar factura" dispara el POST.
 *
 * En éxito: cierra, toast verde, navega a /facturas/:id con breadcrumb
 * contextual para que el "back" vuelva al contrato.
 */
export function GenerarFacturaDialog({
  open, onOpenChange, contrato,
  clienteRazonSocial, planNombre, planPrecio,
}: Props) {
  const navigate = useNavigate();
  const mut = useGenerarFacturaContrato(contrato.id);
  const [step, setStep] = useState<1 | 2>(1);

  // ── Defaults sensatos ────────────────────────────────────────────────
  const hoy = new Date();
  const defaultPeriodoInicio = useMemo(() => {
    const d = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    return toISODate(d);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const defaultPeriodoFin = useMemo(() => {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    return toISODate(d);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const defaultVencimiento = useMemo(() => {
    // día de corte del mes actual; si ya pasó, del próximo mes
    const corte = contrato.fecha_corte;
    const try1 = new Date(hoy.getFullYear(), hoy.getMonth(), corte);
    if (try1 >= new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())) {
      return toISODate(try1);
    }
    const try2 = new Date(hoy.getFullYear(), hoy.getMonth() + 1, corte);
    return toISODate(try2);
  }, [contrato.fecha_corte]);  // eslint-disable-line react-hooks/exhaustive-deps

  // rc142 — el backend dice EXACTAMENTE qué emitiría el poller (próximo corte,
  // periodo = mes del vencimiento, si ya existe). Antes el diálogo proponía el
  // mes EN CURSO con el vencimiento del siguiente → la factura salía "como si
  // fuera el ciclo anterior" y confundía al cliente recién instalado.
  const ciclo = useQuery<ProximoCiclo>({
    queryKey: ["contratos", contrato.id, "proximo-ciclo"],
    queryFn: () => apiCall<ProximoCiclo>("get", `/api/v1/contratos/${contrato.id}/proximo-ciclo`),
    enabled: open,
    staleTime: 30_000,
  });

  // ── Form state ───────────────────────────────────────────────────────
  const [periodoInicio, setPeriodoInicio] = useState(defaultPeriodoInicio);
  const [periodoFin, setPeriodoFin] = useState(defaultPeriodoFin);
  const [fechaVencimiento, setFechaVencimiento] = useState(defaultVencimiento);
  const [descuentoPct, setDescuentoPct] = useState<string>(
    String(contrato.descuento_pct ?? "0"),
  );
  const [formError, setFormError] = useState<string | null>(null);

  // rc142 — al llegar el ciclo del backend, se vuelven los defaults (solo si el
  // usuario no ha tocado nada: step 1 recién abierto).
  useEffect(() => {
    if (!ciclo.data || step !== 1) return;
    setPeriodoInicio(ciclo.data.periodo_inicio);
    setPeriodoFin(ciclo.data.periodo_fin);
    setFechaVencimiento(ciclo.data.vencimiento);
  }, [ciclo.data]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cálculo preview ──────────────────────────────────────────────────
  const precioUnitario = Number(
    contrato.precio_override ?? planPrecio ?? 0,
  );
  const subtotal = precioUnitario;  // 1 ítem con cantidad 1
  const descuento = (subtotal * Number(descuentoPct || 0)) / 100;
  const total = subtotal - descuento;

  function resetAndClose() {
    setStep(1);
    setPeriodoInicio(defaultPeriodoInicio);
    setPeriodoFin(defaultPeriodoFin);
    setFechaVencimiento(defaultVencimiento);
    setDescuentoPct(String(contrato.descuento_pct ?? "0"));
    setFormError(null);
    mut.reset();
    onOpenChange(false);
  }

  function validateAndGoToPreview() {
    setFormError(null);
    if (!periodoInicio || !periodoFin || !fechaVencimiento) {
      setFormError("Todas las fechas son obligatorias.");
      return;
    }
    if (periodoInicio > periodoFin) {
      setFormError("El inicio del período no puede ser posterior al fin.");
      return;
    }
    if (fechaVencimiento < periodoInicio) {
      setFormError("La fecha de vencimiento no puede ser anterior al inicio del período.");
      return;
    }
    const descNum = Number(descuentoPct || 0);
    if (Number.isNaN(descNum) || descNum < 0 || descNum > 100) {
      setFormError("Descuento debe estar entre 0 y 100.");
      return;
    }
    setStep(2);
  }

  async function handleConfirmar() {
    const payload: GenerarFacturaContratoInput = {
      periodo_inicio: periodoInicio,
      periodo_fin: periodoFin,
      fecha_vencimiento: fechaVencimiento,
      descuento_pct: descuentoPct || "0",
    };
    try {
      const factura = await mut.mutateAsync(payload);
      toast.success(`Factura ${factura.folio} generada`, {
        description: `Total ${formatMXN(factura.total)} · vence ${formatDate(factura.fecha_vencimiento)}`,
      });
      resetAndClose();
      // Navegar al detalle con breadcrumb contextual al contrato
      navigate(`/facturas/${factura.id}`, {
        state: {
          from: `/contratos/${contrato.id}`,
          fromLabel: contrato.codigo,
        },
      });
    } catch (err) {
      const apiErr = err as ApiError;
      const mapeado = mapBackendError(apiErr);
      toast.error("No se pudo generar la factura", { description: mapeado });
      // Nos quedamos en el paso 2 para que el usuario pueda retroceder y
      // ajustar si aplica, o cerrar.
    }
  }

  // ── Exentos + cancelados (defensivo, aunque el botón ya debería ocultarse)
  const bloqueantes: { icon: typeof AlertTriangle; label: string }[] = [];
  if (contrato.estatus === "cancelado") {
    bloqueantes.push({
      icon: AlertTriangle,
      label: "Contrato cancelado — no se puede facturar.",
    });
  }
  if (contrato.exento_facturacion) {
    bloqueantes.push({
      icon: AlertTriangle,
      label: "Contrato marcado como exento de facturación.",
    });
  }
  const estaBloqueado = bloqueantes.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : resetAndClose())}
            size="lg" closeOnBackdrop={!mut.isPending}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <Receipt size={16} className="text-primary" />
              Generar factura manual
              <Badge variant="outline" className="ml-1 font-normal">
                Paso {step} / 2
              </Badge>
            </span>
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Ajusta el período y el vencimiento si lo necesitas. Los valores por default vienen del contrato."
              : "Revisa los datos antes de emitir. Esta acción crea la factura y la registra en auditoría."}
          </DialogDescription>
        </DialogHeader>

        {estaBloqueado ? (
          <div className="flex flex-col gap-2 py-4">
            {bloqueantes.map((b, i) => (
              <div key={i} className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
                <b.icon size={14} className="mt-0.5 shrink-0 text-destructive" />
                <span className="text-destructive">{b.label}</span>
              </div>
            ))}
          </div>
        ) : step === 1 ? (
          <>
          {ciclo.data && (
            <div className={`mb-3 flex items-start gap-2 rounded-md border p-3 text-xs ${ciclo.data.ya_emitida ? "border-amber-500/40 bg-amber-500/5" : "border-primary/30 bg-primary/5"}`}>
              {ciclo.data.ya_emitida ? <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" /> : <Info size={14} className="mt-0.5 shrink-0 text-primary" />}
              <span className="text-muted-foreground">
                {ciclo.data.ya_emitida ? (
                  <>Este ciclo <b>ya tiene mensualidad</b> (vence {formatDate(ciclo.data.vencimiento)}). Si emites otra con el mismo mes de vencimiento, el cliente tendrá dos cobros.</>
                ) : (
                  <>Próximo ciclo según el corte (día {ciclo.data.fecha_corte}): periodo <b>{formatDate(ciclo.data.periodo_inicio)} – {formatDate(ciclo.data.periodo_fin)}</b>, vence <b>{formatDate(ciclo.data.vencimiento)}</b>
                  {ciclo.data.poller_lo_emitiria_hoy ? " — el poller la emitiría hoy de todos modos." : ` — el poller la emitirá en ${Math.max(0, ciclo.data.dias_restantes - ciclo.data.dias_anticipacion)} día(s).`}</>
                )}
              </span>
            </div>
          )}
          <Step1Form
            contrato={contrato}
            periodoInicio={periodoInicio} setPeriodoInicio={setPeriodoInicio}
            periodoFin={periodoFin} setPeriodoFin={setPeriodoFin}
            fechaVencimiento={fechaVencimiento} setFechaVencimiento={setFechaVencimiento}
            descuentoPct={descuentoPct} setDescuentoPct={setDescuentoPct}
            error={formError}
          />
          </>
        ) : (
          <Step2Preview
            contrato={contrato}
            clienteRazonSocial={clienteRazonSocial}
            planNombre={planNombre}
            periodoInicio={periodoInicio}
            periodoFin={periodoFin}
            fechaVencimiento={fechaVencimiento}
            precioUnitario={precioUnitario}
            subtotal={subtotal}
            descuentoPct={Number(descuentoPct || 0)}
            descuento={descuento}
            total={total}
          />
        )}

        <DialogFooter>
          {estaBloqueado ? (
            <Button size="sm" variant="outline" onClick={resetAndClose}>
              Cerrar
            </Button>
          ) : step === 1 ? (
            <>
              <Button size="sm" variant="ghost" onClick={resetAndClose}>
                Cancelar
              </Button>
              <Button size="sm" onClick={validateAndGoToPreview}>
                Revisar <ArrowRight size={12} />
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm" variant="ghost"
                onClick={() => setStep(1)} disabled={mut.isPending}
              >
                <ArrowLeft size={12} /> Atrás
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={resetAndClose} disabled={mut.isPending}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmar}
                disabled={mut.isPending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <CheckCircle2 size={12} />
                {mut.isPending ? "Generando…" : "Sí, generar factura"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Paso 1 — Form
// ══════════════════════════════════════════════════════════════════════════
type Step1Props = {
  contrato: ContratoDetail;
  periodoInicio: string; setPeriodoInicio: (v: string) => void;
  periodoFin: string; setPeriodoFin: (v: string) => void;
  fechaVencimiento: string; setFechaVencimiento: (v: string) => void;
  descuentoPct: string; setDescuentoPct: (v: string) => void;
  error: string | null;
};

function Step1Form(p: Step1Props) {
  return (
    <div className="flex flex-col gap-3 py-2">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field icon={Calendar} label="Periodo — inicio">
          <Input
            type="date" value={p.periodoInicio}
            onChange={(e) => p.setPeriodoInicio(e.target.value)}
          />
        </Field>
        <Field icon={Calendar} label="Periodo — fin">
          <Input
            type="date" value={p.periodoFin}
            onChange={(e) => p.setPeriodoFin(e.target.value)}
          />
        </Field>
        <Field
          icon={Calendar} label="Fecha de vencimiento"
          hint={`Corte del contrato: día ${p.contrato.fecha_corte}`}
        >
          <Input
            type="date" value={p.fechaVencimiento}
            onChange={(e) => p.setFechaVencimiento(e.target.value)}
          />
        </Field>
        <Field
          icon={Percent} label="Descuento %"
          hint={
            Number(p.contrato.descuento_pct) > 0
              ? `Contrato tiene ${p.contrato.descuento_pct}% permanente`
              : "0 = sin descuento"
          }
        >
          <Input
            type="number" min={0} max={100} step={0.01}
            value={p.descuentoPct}
            onChange={(e) => p.setDescuentoPct(e.target.value)}
          />
        </Field>
      </div>
      {p.error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>{p.error}</span>
        </div>
      )}
      <div className="flex items-start gap-2 rounded-md border border-muted bg-muted/30 p-2 text-[11px] text-muted-foreground">
        <Info size={12} className="mt-0.5 shrink-0" />
        <span>
          Si dejas algo en blanco, el sistema usará los valores por default del contrato
          (periodo del mes actual + vencimiento al día de corte).
        </span>
      </div>
    </div>
  );
}

function Field({
  icon: Icon, label, hint, children,
}: {
  icon: typeof Calendar; label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
        <Icon size={11} /> {label}
      </label>
      {children}
      {hint && <span className="text-[10px] text-muted-foreground">{hint}</span>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Paso 2 — Preview
// ══════════════════════════════════════════════════════════════════════════
type Step2Props = {
  contrato: ContratoDetail;
  clienteRazonSocial?: string;
  planNombre?: string;
  periodoInicio: string;
  periodoFin: string;
  fechaVencimiento: string;
  precioUnitario: number;
  subtotal: number;
  descuentoPct: number;
  descuento: number;
  total: number;
};

function Step2Preview(p: Step2Props) {
  return (
    <div className="flex flex-col gap-3 py-2">
      {/* Quién */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <PreviewCell
          icon={UserIcon}
          label="Cliente"
          value={p.clienteRazonSocial ?? "—"}
        />
        <PreviewCell
          icon={FileText}
          label="Contrato"
          value={
            <span className="font-mono text-sm">{p.contrato.codigo}</span>
          }
        />
      </div>

      {/* Qué + cuándo */}
      <div className="rounded-lg border bg-card p-3">
        <h4 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
          Concepto
        </h4>
        <div className="text-sm font-medium">
          Servicio de Internet{p.planNombre ? ` ${p.planNombre}` : ""} — {p.contrato.codigo}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          Período {formatDate(p.periodoInicio)} → {formatDate(p.periodoFin)}
        </div>
        <div className="mt-0.5 text-xs">
          <span className="text-muted-foreground">Vence: </span>
          <span className="font-medium">{formatDate(p.fechaVencimiento)}</span>
        </div>
      </div>

      {/* Desglose */}
      <div className="rounded-lg border bg-card p-3">
        <h4 className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
          Desglose
        </h4>
        <dl className="grid grid-cols-[1fr_auto] gap-y-1 text-sm">
          <dt className="text-muted-foreground">Subtotal</dt>
          <dd className="text-right font-mono">{formatMXN(p.subtotal)}</dd>

          <dt className="text-muted-foreground">
            Descuento {p.descuentoPct > 0 && `(${p.descuentoPct}%)`}
          </dt>
          <dd className="text-right font-mono">
            {p.descuento > 0 ? `− ${formatMXN(p.descuento)}` : formatMXN(0)}
          </dd>

          <dt className="mt-1 border-t pt-1.5 text-sm font-semibold">Total</dt>
          <dd className="mt-1 border-t pt-1.5 text-right text-base font-bold text-foreground">
            {formatMXN(p.total)}
          </dd>
        </dl>
      </div>

      {/* Warning si total=0 — probablemente plan sin precio configurado */}
      {p.total === 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 p-2.5 text-[11px]">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-500" />
          <div className="flex flex-col gap-1">
            <span className="font-medium text-amber-800 dark:text-amber-300">
              El total sale en $0.00
            </span>
            <span className="text-amber-700/90 dark:text-amber-400/90">
              Verifica que el <strong>plan tenga precio configurado</strong> (ve a /planes
              y edita el plan) o que el contrato tenga un <strong>precio override</strong>.
              Si emites con $0, la factura quedará como pagada de inmediato.
            </span>
          </div>
        </div>
      )}

      {/* Aviso auditoría */}
      <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 p-2 text-[11px] text-muted-foreground">
        <Info size={12} className="mt-0.5 shrink-0 text-primary" />
        <span>
          Al confirmar, la emisión queda registrada en auditoría con tu usuario
          como responsable. La factura puede anularse después desde su detalle.
        </span>
      </div>
    </div>
  );
}

function PreviewCell({
  icon: Icon, label, value,
}: {
  icon: typeof UserIcon; label: string; value: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        <Icon size={11} />
        <span>{label}</span>
      </div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
function mapBackendError(err: ApiError): string {
  switch (err.code) {
    case "contrato_cancelado":
      return "El contrato está cancelado; no se puede emitir factura.";
    case "contrato_exento_facturacion":
      return "El contrato está marcado como exento de facturación (cliente gratis).";
    case "invalid_vencimiento":
      return "La fecha de vencimiento es inválida (menor a la emisión).";
    case "cliente_not_found":
      return "Cliente del contrato no encontrado.";
    default:
      return err.message ?? "Error desconocido del servidor.";
  }
}
